import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  InputSignal,
  InputSignalWithTransform,
  OnInit,
  Signal
} from '@angular/core';
import { ControlContainer, FormGroupDirective, ReactiveFormsModule } from '@angular/forms';

import { UntilDestroy } from '@ngneat/until-destroy';
import { Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';

import {
  TiimeAlertModule,
  TiimeButtonModule,
  TiimeFormModule,
  TiimeIconModule,
  TiimeInputContainerModule,
  TiimeRadioModule,
  TiimeSnackbarService,
  TiimeTooltipModule
} from 'tiime-material';

import { ParametersStepForm, ParametersStepLabelModeType } from '@forms/create-company-form';
import { LabelCategoryPlan } from '@models/label-category-plan';
import { LabelsPlan } from '@models/labels-plan';
import { BooleanInputType } from '@modules/core/types/boolean-input.type';
import { LabelCategoryPlanSetupDialogModule } from '@shared-dialogs/label-category-plan-setup-dialog/label-category-plan-setup-dialog.module';
import { LabelCategoryPlanSetupDialogService } from '@shared-dialogs/label-category-plan-setup-dialog/label-category-plan-setup-dialog.service';
import { LabelCategoryPlanSelectComponent } from '@shared-selectors/label-category-plan-select/label-category-plan-select.component';
import { LabelPlanSelectComponent } from '@shared-selectors/label-plan-select/label-plan-select.component';

@UntilDestroy({ checkProperties: true })
@Component({
  standalone: true,
  selector: 'app-company-initialization-form',
  templateUrl: './company-initialization-form.component.html',
  styleUrls: ['./company-initialization-form.component.scss'],
  imports: [
    ReactiveFormsModule,
    TiimeIconModule,
    TiimeTooltipModule,
    TiimeButtonModule,
    TiimeFormModule,
    TiimeInputContainerModule,
    TiimeRadioModule,
    TiimeAlertModule,
    LabelCategoryPlanSelectComponent,
    LabelPlanSelectComponent,
    LabelCategoryPlanSetupDialogModule
  ],
  changeDetection: ChangeDetectionStrategy.Default,
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }]
})
export class CompanyInitializationFormComponent implements OnInit {
  readonly labelsPlans: InputSignal<LabelsPlan[]> = input.required<LabelsPlan[]>();
  readonly labelCategoryPlans: InputSignal<LabelCategoryPlan[]> = input.required<LabelCategoryPlan[]>();
  readonly withFEC: InputSignalWithTransform<boolean, BooleanInputType> = input<boolean, BooleanInputType>(true, {
    transform: booleanAttribute
  });

  readonly errorMessage: Signal<string> = computed(() =>
    this.withFEC()
      ? `Veuillez sélectionner un plan de labels existant ou importer un fichier FEC.`
      : `Veuillez sélectionner un plan de labels existant.`
  );

  parametersStepForm: ParametersStepForm;

  readonly acceptedTypes: string[] = [
    'text/plain',
    'text/csv',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.oasis.opendocument.spreadsheet',
    'application/pdf'
  ];
  readonly acceptedTypesLabel: string = 'Format accepté : txt, csv, xls, xslx, ods, pdf';

  private readonly subscription: Subscription = new Subscription();

  constructor(
    private readonly tiimeSnackbarService: TiimeSnackbarService,
    private readonly labelCategoryPlanSetupDialogService: LabelCategoryPlanSetupDialogService,
    private readonly formGroupDirective: FormGroupDirective
  ) {}

  ngOnInit(): void {
    this.parametersStepForm = this.formGroupDirective.control as ParametersStepForm;
    this.observeLabelModeChanges();
  }

  uploadFEC(files: FileList): void {
    if (files.length === 0) {
      return;
    }

    const file = files[0];

    if (!this.acceptedTypes.includes(file.type)) {
      this.tiimeSnackbarService.openError(`Fichier invalide. ${this.acceptedTypesLabel}`);
      return;
    }

    this.parametersStepForm.fecFile.setValue(file);
  }

  resetFecFile(): void {
    this.parametersStepForm.fecFile.reset();
  }

  resetCategorySetupPlan(): void {
    this.parametersStepForm.categorySetupPlan.reset();
  }

  resetLabelPlan(): void {
    this.parametersStepForm.labelPlan.reset();
  }

  openLabelCategoryPlanSetupDialog(): void {
    this.labelCategoryPlanSetupDialogService
      .open({ data: this.parametersStepForm.controls.categorySetupPlan.value || null })
      .afterClosed()
      .subscribe();
  }

  private observeLabelModeChanges(): void {
    this.subscription.add(
      this.parametersStepForm.labelMode.valueChanges
        .pipe(
          tap((labelMode: ParametersStepLabelModeType) => {
            if (labelMode === 'labelPlan') {
              this.resetFecFile();
              return;
            }

            if (labelMode === 'fecFile') {
              this.resetLabelPlan();
              return;
            }
          })
        )
        .subscribe()
    );
  }
}
